import type { SvgIconComponent } from '@mui/icons-material';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import Apartment from '@mui/icons-material/Apartment';
import ContactPhoneOutlined from '@mui/icons-material/ContactPhoneOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Hub from '@mui/icons-material/Hub';
import KeyOutlined from '@mui/icons-material/KeyOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import PinDropOutlined from '@mui/icons-material/PinDropOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import SellOutlined from '@mui/icons-material/SellOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import Tune from '@mui/icons-material/Tune';

export type EntityId = 'fc' | 'tenant' | 'origin' | 'contract' | 'carrier';
/** Fulfillment-context child components; selecting one highlights it under the FC. */
export type ChildKey = 'labels' | 'augs' | 'tpb' | 'inj' | 'tags';
export type PaletteKey = 'primary' | 'secondary' | 'info' | 'warning' | 'error';
export type Cardinality = 'Single' | 'Multiple' | 'Per origin';

export interface ContainedItem {
  /** Set on FC items that have a child node in the map, so selecting the node highlights the row. */
  key?: ChildKey;
  Icon: SvgIconComponent;
  label: string;
  desc: string;
  card: Cardinality;
}

export interface Relationship {
  verb: string;
  target: EntityId;
  card: string;
}

export interface Entity {
  id: EntityId;
  label: string;
  sub: string;
  level: string;
  color: PaletteKey;
  Icon: SvgIconComponent;
  /** Center in map coordinates (MAP_W × MAP_H). */
  x: number;
  y: number;
  desc: string;
  items: ContainedItem[];
  rels: Relationship[];
}

export interface Edge {
  from: EntityId;
  to: EntityId;
  path: string;
  card: string;
  labelX: number;
  labelY: number;
  dashed?: boolean;
}

export interface ChildNode {
  key: ChildKey;
  label: string;
  sub: string;
  x: number;
}

export interface Selection {
  id: EntityId;
  focus: ChildKey | null;
}

export type SelectHandler = (id: EntityId, focus?: ChildKey) => void;

export const MAP_W = 1000;
export const MAP_H = 520;
export const CHILD_Y = 440;
export const INITIAL_SELECTION: Selection = { id: 'fc', focus: null };

export const ENTITIES: Record<EntityId, Entity> = {
  fc: {
    id: 'fc',
    label: 'Fulfillment context',
    sub: 'Central component',
    level: 'CENTRAL',
    color: 'primary',
    Icon: Hub,
    x: 500,
    y: 250,
    desc: 'The central configuration unit. It brings together where orders ship from, which carriers and contracts apply, and how labels are produced.',
    items: [
      { Icon: LocationOnOutlined, label: 'Origin', desc: 'Where fulfillment happens', card: 'Single' },
      { Icon: DescriptionOutlined, label: 'Contracts', desc: 'Carrier contracts and rate setup', card: 'Multiple' },
      { key: 'labels', Icon: LabelOutlined, label: 'Label properties', desc: 'Format and printing specs', card: 'Single' },
      { key: 'augs', Icon: Tune, label: 'Label augmentations', desc: 'Custom label modifications', card: 'Multiple' },
      { key: 'tpb', Icon: AccountBalanceOutlined, label: 'Third-party billing', desc: 'Billing account sets', card: 'Multiple' },
      { key: 'inj', Icon: SettingsOutlined, label: 'Injection profiles', desc: 'Processing and handling instructions', card: 'Multiple' },
      { key: 'tags', Icon: SellOutlined, label: 'Tags', desc: 'Custom metadata for filtering', card: 'Multiple' },
    ],
    rels: [
      { verb: 'May belong to a', target: 'tenant', card: '0..1' },
      { verb: 'Ships from exactly one', target: 'origin', card: '1' },
      { verb: 'Uses one or more', target: 'contract', card: '1..*' },
    ],
  },
  tenant: {
    id: 'tenant',
    label: 'Tenant',
    sub: 'Customer org',
    level: 'CUSTOMER',
    color: 'secondary',
    Icon: Apartment,
    x: 140,
    y: 80,
    desc: 'A customer organization that groups related fulfillment contexts, such as business units or product lines.',
    items: [
      { Icon: Hub, label: 'Fulfillment contexts', desc: 'Business units with their own fulfillment needs', card: 'Multiple' },
      { Icon: SettingsOutlined, label: 'Origin info map', desc: 'Return address and contact per origin', card: 'Per origin' },
    ],
    rels: [
      { verb: 'Groups zero or more', target: 'fc', card: '0..*' },
      { verb: 'Shares origins through', target: 'fc', card: 'via' },
    ],
  },
  origin: {
    id: 'origin',
    label: 'Origin',
    sub: 'Location',
    level: 'LOCATION',
    color: 'info',
    Icon: LocationOnOutlined,
    x: 140,
    y: 250,
    desc: 'A physical warehouse or store where fulfillment operations happen. Not owned by a tenant; shared across fulfillment contexts.',
    items: [
      { Icon: PinDropOutlined, label: 'Address', desc: 'Street, city, state, postal code, country', card: 'Single' },
      { Icon: ContactPhoneOutlined, label: 'Contact', desc: 'Name, phone and email', card: 'Single' },
      { Icon: ScheduleOutlined, label: 'Schedule', desc: 'Pickup schedules and availability', card: 'Single' },
      { Icon: LocalShippingOutlined, label: 'Carrier schedules', desc: 'Carrier pickup times and cutoffs', card: 'Multiple' },
    ],
    rels: [{ verb: 'Referenced by one or more', target: 'fc', card: '1..*' }],
  },
  contract: {
    id: 'contract',
    label: 'Contract',
    sub: 'Carrier service',
    level: 'SERVICE',
    color: 'warning',
    Icon: DescriptionOutlined,
    x: 860,
    y: 140,
    desc: 'Defines the rates, service methods and account a fulfillment context uses with a carrier.',
    items: [
      { Icon: LocalShippingOutlined, label: 'Carrier account', desc: 'Integration details and credentials', card: 'Single' },
      { Icon: RequestQuoteOutlined, label: 'Carrier rates', desc: 'Rates per service method', card: 'Multiple' },
      { Icon: PinDropOutlined, label: 'Return-to address', desc: 'Return shipping address', card: 'Single' },
      { Icon: ContactPhoneOutlined, label: 'Return-to contact', desc: 'Return shipping contact', card: 'Single' },
    ],
    rels: [
      { verb: 'Belongs to a', target: 'fc', card: '1' },
      { verb: 'References one', target: 'carrier', card: '1' },
    ],
  },
  carrier: {
    id: 'carrier',
    label: 'Carrier account',
    sub: 'Integration',
    level: 'INTEGRATION',
    color: 'error',
    Icon: LocalShippingOutlined,
    x: 860,
    y: 340,
    desc: 'Integration configuration for a specific shipping carrier. Shared across contracts; not owned by a tenant.',
    items: [
      { Icon: KeyOutlined, label: 'Credentials', desc: 'API keys, account numbers, auth details', card: 'Multiple' },
      { Icon: LocalShippingOutlined, label: 'Service methods', desc: 'Available shipping services', card: 'Multiple' },
      { Icon: PinDropOutlined, label: 'Return-to address', desc: 'Default return address', card: 'Single' },
      { Icon: ContactPhoneOutlined, label: 'Return-to contact', desc: 'Default return contact', card: 'Single' },
    ],
    rels: [{ verb: 'Referenced by one or more', target: 'contract', card: '1..*' }],
  },
};

export const ENTITY_LIST: readonly Entity[] = Object.values(ENTITIES);

export const EDGES: readonly Edge[] = [
  { from: 'tenant', to: 'fc', path: 'M220,80 C360,80 420,150 470,210', card: '0..*', labelX: 360, labelY: 112, dashed: true },
  { from: 'fc', to: 'origin', path: 'M400,250 L221,250', card: '1', labelX: 312, labelY: 250 },
  { from: 'fc', to: 'contract', path: 'M600,240 C700,230 740,145 779,141', card: '1..*', labelX: 712, labelY: 186 },
  { from: 'contract', to: 'carrier', path: 'M860,170 L860,310', card: '1', labelX: 860, labelY: 240 },
];

export const CHILD_NODES: readonly ChildNode[] = [
  { key: 'labels', label: 'Labels', sub: 'Printing', x: 230 },
  { key: 'augs', label: 'Label augs', sub: 'Customization', x: 365 },
  { key: 'inj', label: 'Injection', sub: 'Profiles', x: 500 },
  { key: 'tpb', label: 'Third party', sub: 'Billing', x: 635 },
  { key: 'tags', label: 'Tags', sub: 'Metadata', x: 770 },
];

export const childEdgePath = (x: number) => `M500,289 C500,350 ${x},360 ${x},413`;

/** The selected entity plus every entity one edge away from it. */
export const relatedIds = (selected: EntityId): Set<EntityId> => {
  const ids = new Set<EntityId>([selected]);
  EDGES.forEach(({ from, to }) => {
    if (from === selected) ids.add(to);
    if (to === selected) ids.add(from);
  });
  return ids;
};

/** Map coordinate → CSS percentage of the map box. */
export const pct = (value: number, of: number) => `${((value / of) * 100).toFixed(2)}%`;
